/**
 * Pièces en attente configurable components (Phase 8):
 * `extranet.pendingPiecesListCard` — the « En attente » list of
 * `WS2_EA_TAB_PMQ` (fiche 22780).
 *
 * Layout rules target the stable item keys `libelle`, `statut`,
 * `lienDossier`, `dateDemande` (`./pendingPieceItems.ts`) through the
 * injected `_layoutOverrides`; the registered `propsSchema` only describes
 * the data props.
 */

import { z } from 'zod';

import {
  withComponentRules,
  type ContextVariableDescriptor,
} from '@components/shared/configurable';

import { extranetBaseContextSchema } from '../../contextVariables';
import { EXTRANET_MODULE, registerOnce } from '../registerHelpers';
import { SHARED_CARD_PROP_LABELS } from '../shared';
import {
  PendingPiecesListCard,
  type PendingPiecesListCardProps,
} from './PendingPiecesListCard';
import { PENDING_PIECES_LIST_CARD_ID } from './pendingPieceItems';

/** Props a page passes to `extranet.pendingPiecesListCard`. */
export type PendingPiecesListCardDataProps = Pick<
  PendingPiecesListCardProps,
  'pieces' | 'loading' | 'error'
>;

/** One pending piece (`PendingPiece` view model of `api/piecesService`). */
export const pendingPieceSchema = z.object({
  id: z.string(),
  libelle: z.string().nullable(),
  dateDemande: z.string().nullable(),
  statut: z.string().nullable(),
  origine: z.enum(['POLICE', 'SINISTRE']).nullable(),
  reference: z.string().nullable(),
  idNpol: z.string().nullable(),
  idSin: z.string().nullable(),
  numPol: z.string().nullable(),
  insured: z.string().nullable(),
});

export const pendingPiecesListCardPropsSchema = z.object({
  pieces: z.array(pendingPieceSchema),
  loading: z.boolean().optional(),
  error: z.string().nullable().optional(),
}) satisfies z.ZodType<PendingPiecesListCardDataProps>;

/**
 * Rule-context allow-list of the « Pièces à fournir » page: the extranet
 * base variables + the number of pending pieces.
 */
export const pendingPiecesContextSchema = {
  ...extranetBaseContextSchema,
  pending_pieces_count: {
    type: 'number',
    description:
      'Number of pending pieces listed on the page (WS2_EA_TAB_PMQ rows); ' +
      'undefined while loading or on error.',
    sourceField: 'WS2_EA_TAB_PMQ',
  },
} as const satisfies Record<string, ContextVariableDescriptor>;

/** Sample props of the admin config page's preview. */
export const PENDING_PIECES_LIST_PREVIEW_PROPS: PendingPiecesListCardDataProps = {
  pieces: [
    {
      id: '2266',
      libelle: 'Certificat Q18 Installation électriques contrôlées annuellement',
      dateDemande: '13/05/2026',
      statut: 'EN ATTENTE',
      origine: 'POLICE',
      reference: 'Contrat 1237452',
      idNpol: '9894',
      idSin: null,
      numPol: '1237452',
      insured: 'A CIEL OUVERT GRAND SUD OUEST',
    },
    {
      id: '2301',
      libelle: 'Constat amiable',
      dateDemande: '02/06/2026',
      statut: 'EN ATTENTE',
      origine: 'SINISTRE',
      reference: 'Sinistre 2026-0042',
      idNpol: '9894',
      idSin: '52011',
      numPol: '1237452',
      insured: 'A CIEL OUVERT GRAND SUD OUEST',
    },
  ],
};

/** Register the Pièces components. Idempotent. */
export function registerPiecesComponents(): void {
  registerOnce<PendingPiecesListCardDataProps>({
    id: PENDING_PIECES_LIST_CARD_ID,
    displayName: 'Liste des pièces à fournir',
    module: EXTRANET_MODULE,
    component: PendingPiecesListCard,
    propsSchema: pendingPiecesListCardPropsSchema,
    defaultPropLabels: {
      pieces: SHARED_CARD_PROP_LABELS.pieces,
      loading: SHARED_CARD_PROP_LABELS.loading,
      error: SHARED_CARD_PROP_LABELS.error,
    },
    previewProps: PENDING_PIECES_LIST_PREVIEW_PROPS,
    contextVariables: pendingPiecesContextSchema,
  });
}

/** Rule-aware pending pieces list — pages import it aliased to `PendingPiecesListCard`. */
export const PendingPiecesListCardConfigurable = withComponentRules(
  PendingPiecesListCard,
  PENDING_PIECES_LIST_CARD_ID,
);
